/**
 * @copyright Copyright 2021-2025 Epic Systems Corporation
 * @file Agreement modal that shows an agreement PDF and accept decline options
 * @module Epic.AppOrchard.Core.AgreementModal
 */

import { WarningIcon } from "@chakra-ui/icons";
import {
	Box,
	Button,
	Checkbox,
	Divider,
	Flex,
	FormControl,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Spacer,
	Text,
} from "@chakra-ui/react";
import { useUserSecurityState } from "ao/state/userSecurity";
import { UserAccountType } from "ao/types";
import React, { ChangeEvent, FC, memo } from "react";
import { LinkButton } from ".";

interface IProps {
	isOpen: boolean;
	/* modal header */
	header: React.ReactNode;
	termsUrl: string;
	/* URL where terms can be downloaded */
	downloadTermsUrl: string;
	/* describe the terms document for users who are using a screenreader */
	termsAriaLabel: string;
	/* caption of the accept button */
	acceptCaption: string;
	/* text to show beneath the accept button, e.g. to explain the effect of accepting terms */
	acceptEffectText?: string;
	additionalDetails?: React.ReactNode;
	cancelCaption?: string;
	/* callback for closing the modal */
	onClose: () => void;
	onAcceptCheckedChanged?: (ev: ChangeEvent<HTMLInputElement>) => void;
	acceptChecked?: boolean;
	onAgree: () => void;
	downloadFileName: string;
	/** if true, show terms but don't allow/require signing them */
	skipSigning?: boolean;
	acceptButtonDownloadsAgreement?: boolean;
}

export const AgreementModal: FC<IProps> = memo((props: IProps) => {
	const {
		isOpen,
		header,
		termsUrl,
		termsAriaLabel,
		downloadTermsUrl,
		acceptCaption,
		acceptEffectText,
		acceptChecked,
		additionalDetails,
		cancelCaption,
		onClose,
		onAcceptCheckedChanged,
		onAgree,
		skipSigning,
		downloadFileName,
	} = props;

	const { userSecurity } = useUserSecurityState((selectors) => selectors.getState(), []);

	const isExpandedConsultant = userSecurity.userAccountType === UserAccountType.ExpandedConsultant;
	const requireSigning = skipSigning !== true;
	const acceptButtonDownloadsAgreement = props.acceptButtonDownloadsAgreement ?? false;

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="6xl" preserveScrollBarGap isCentered>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>{header}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<object
						type="application/pdf"
						data={termsUrl}
						aria-label={termsAriaLabel}
						style={{ width: "100%", height: "480px" }}
					></object>
					{additionalDetails && <Text mt="1em">{additionalDetails}</Text>}
					{requireSigning && (
						<>
							<FormControl id="AgreeToTerms" mt="0.75em" mb="0.5em">
								<Checkbox
									isChecked={acceptChecked}
									isDisabled={isExpandedConsultant}
									onChange={onAcceptCheckedChanged}
									title={
										isExpandedConsultant
											? "Expanded consultants cannot agree to terms"
											: ""
									}
								>
									I agree to the terms outlined above
								</Checkbox>
							</FormControl>
						</>
					)}
				</ModalBody>
				<Divider />
				<ModalFooter display="block" pb="1em" pt="1em">
					<Flex>
						<LinkButton
							url={downloadTermsUrl}
							download={downloadFileName}
							mr="2em"
							minW="fit-content"
						>
							Download Agreement
						</LinkButton>
						<Spacer />
						<Box>
							<Flex>
								{acceptButtonDownloadsAgreement ? (
									<LinkButton
										colorScheme="blue"
										color="white"
										onClick={onAgree}
										disabled={requireSigning && !acceptChecked}
										title={
											requireSigning && !acceptChecked
												? "You need to agree to the terms before continuing"
												: ""
										}
										mr={2}
										url={downloadTermsUrl}
										download={downloadFileName}
									>
										{acceptCaption}
									</LinkButton>
								) : (
									<Button
										colorScheme="blue"
										onClick={onAgree}
										disabled={requireSigning && !acceptChecked}
										title={
											requireSigning && !acceptChecked
												? "You need to agree to the terms before continuing"
												: ""
										}
										mr={2}
									>
										{acceptCaption}
									</Button>
								)}

								<Button onClick={onClose}>{cancelCaption || "Cancel"}</Button>
							</Flex>
							{isExpandedConsultant && requireSigning ? (
								<Text mt="1em">
									<WarningIcon w={4} h={4} mr="4px" color="orange.500" />
									Expanded consultants are unable to sign agreements for their organization.
									Ask a non-consultant user with the appropriate security at your
									organization to accept this agreement.
								</Text>
							) : (
								<>{acceptEffectText && <Text mt="1em">{acceptEffectText}</Text>}</>
							)}
						</Box>
					</Flex>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
});
